/* eslint-disable react-refresh/only-export-components */
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useListPanelSearch } from '@/hooks/lib/useListPanelState';
import { useRouter } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  Ban,
  Bookmark,
  BookmarkPlus,
  BriefcaseBusiness,
  FileText,
  GitMerge,
  MoreVertical,
  Pencil,
  Printer,
  Trash2,
  XCircle,
  Zap,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { EntityListCompactItem, EntitySplitListPanel } from '@/components/organisms';
import { DataTableFilterSection } from '@/components/organisms/DataTableFilterSection';
import {
  DataTableDateRangeField,
  DataTableNumberFilterField,
  DataTableSelectFilterField,
  EntityListActiveDatePill,
  EntityListAdvancedFiltersSheet,
  EntityRowActions,
  type DataTableNumberOperator,
} from '@/components/molecules';
import { useLeadStages } from '@/hooks/leads/useLeadStages';
import { useLeads } from '@/hooks/leads/useLeads';
import {
  useCreateLeadSavedView,
  useDeleteLeadSavedView,
  useLeadSavedViews,
} from '@/hooks/leads/useLeadSavedViews';
import { useConvertLeadToJob } from '@/hooks/leads/useConvertLeadToJob';
import { useAutoAssignLead } from '@/hooks/leads/useAutoAssignLead';
import { useLeadAssigneeFilterOptions } from '@/hooks/leads/useLeadAssigneeFilterOptions';
import { DisqualifyLeadModal } from '@/components/organisms/leads/DisqualifyLeadModal';
import { MarkLeadLostModal } from '@/components/organisms/leads/MarkLeadLostModal';
import { MergeLeadModal } from '@/components/organisms/leads/MergeLeadModal';
import { LEAD_SOURCE_FILTERS, type LeadSourceFilter } from '@/lib/constants/lead-sources';
import { resolveLeadValueFact } from '@/lib/leads/leadFormat';
import type { Lead, LeadListParams, LeadSavedViewFilters } from '@/types/lead';

function formatMoney(value?: number | null) {
  if (value == null) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function getContactName(lead: Lead) {
  return (
    lead.contact?.displayName ??
    [lead.contact?.firstName, lead.contact?.lastName].filter(Boolean).join(' ') ??
    ''
  );
}

function leadTitle(lead: Lead) {
  return lead.title || lead.serviceRequested || 'Untitled lead';
}

function isGeneratedLeadTitle(lead: Lead) {
  return lead.title?.trim().toLowerCase() === `lead ${lead.leadNumber}`.toLowerCase();
}

type SortOption = {
  label: string;
  sortBy:
    | 'createdAt'
    | 'leadNumber'
    | 'leadScore'
    | 'value'
    | 'followUpDate'
    | 'lastActivityAt'
    | 'slaDeadline'
    | 'priority';
  sortDir: 'asc' | 'desc';
  icon: ReactNode;
};

export type LeadStageFilter = string | 'all';
export type LeadAssigneeFilter = string | 'all';
type LeadPriorityFilter = string | 'all';
type LeadGradeFilter = 'A' | 'B' | 'C' | 'D' | 'F' | 'all';
type LeadSlaFilter = 'BREACHED' | 'MET' | 'PENDING' | 'all';

export interface LeadListFilters {
  q: string;
  stage: LeadStageFilter;
  source: LeadSourceFilter;
  /**
   * `'all'`, a user id, or `UNASSIGNED_LEAD_FILTER` for leads with no owner.
   * The sentinel is a filter value the server understands, not a UI-only
   * placeholder — see the constant.
   */
  assignedTo: LeadAssigneeFilter;
  priority: LeadPriorityFilter;
  leadGrade: LeadGradeFilter;
  qualificationStatus: string;
  slaStatus: LeadSlaFilter;
  estimatedValueOp: DataTableNumberOperator;
  estimatedValueValue: string;
  page: number;
  sort: number;
  dateFrom: string | undefined;
  dateTo: string | undefined;
}

export const DEFAULT_LEAD_LIST_FILTERS: LeadListFilters = {
  q: '',
  stage: 'all',
  source: 'all',
  assignedTo: 'all',
  priority: 'all',
  leadGrade: 'all',
  qualificationStatus: 'all',
  slaStatus: 'all',
  estimatedValueOp: 'eq',
  estimatedValueValue: '',
  page: 1,
  sort: 0,
  dateFrom: undefined,
  dateTo: undefined,
};

export function resolveLeadListFilters(search: Partial<LeadListFilters>): LeadListFilters {
  return {
    q: search.q ?? DEFAULT_LEAD_LIST_FILTERS.q,
    stage: search.stage ?? DEFAULT_LEAD_LIST_FILTERS.stage,
    source: search.source ?? DEFAULT_LEAD_LIST_FILTERS.source,
    assignedTo: search.assignedTo ?? DEFAULT_LEAD_LIST_FILTERS.assignedTo,
    priority: search.priority ?? DEFAULT_LEAD_LIST_FILTERS.priority,
    leadGrade: search.leadGrade ?? DEFAULT_LEAD_LIST_FILTERS.leadGrade,
    qualificationStatus:
      search.qualificationStatus ?? DEFAULT_LEAD_LIST_FILTERS.qualificationStatus,
    slaStatus: search.slaStatus ?? DEFAULT_LEAD_LIST_FILTERS.slaStatus,
    estimatedValueOp: search.estimatedValueOp ?? DEFAULT_LEAD_LIST_FILTERS.estimatedValueOp,
    estimatedValueValue:
      search.estimatedValueValue ?? DEFAULT_LEAD_LIST_FILTERS.estimatedValueValue,
    page: search.page ?? DEFAULT_LEAD_LIST_FILTERS.page,
    sort: search.sort ?? DEFAULT_LEAD_LIST_FILTERS.sort,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
  };
}

export type LeadListSearch = Partial<LeadListFilters>;

export function cleanLeadListSearch(filters: LeadListFilters): LeadListSearch {
  const cleaned: LeadListSearch = {};

  // Keep the raw query (untrimmed) so the controlled search input can hold a
  // trailing space while the user is typing a multi-word term (e.g. "Customer
  // Name"). Trimming here stripped the space on every keystroke round-trip, so
  // spaces could never be entered. The guard still omits whitespace-only queries;
  // the value is trimmed where it's sent to the API (see buildLeadListParams).
  if (filters.q.trim()) cleaned.q = filters.q;
  if (filters.stage !== DEFAULT_LEAD_LIST_FILTERS.stage) cleaned.stage = filters.stage;
  if (filters.source !== DEFAULT_LEAD_LIST_FILTERS.source) cleaned.source = filters.source;
  if (filters.assignedTo !== DEFAULT_LEAD_LIST_FILTERS.assignedTo) {
    cleaned.assignedTo = filters.assignedTo;
  }
  if (filters.priority !== DEFAULT_LEAD_LIST_FILTERS.priority) cleaned.priority = filters.priority;
  if (filters.leadGrade !== DEFAULT_LEAD_LIST_FILTERS.leadGrade)
    cleaned.leadGrade = filters.leadGrade;
  if (filters.qualificationStatus !== DEFAULT_LEAD_LIST_FILTERS.qualificationStatus) {
    cleaned.qualificationStatus = filters.qualificationStatus;
  }
  if (filters.slaStatus !== DEFAULT_LEAD_LIST_FILTERS.slaStatus) {
    cleaned.slaStatus = filters.slaStatus;
  }
  if (filters.estimatedValueValue.trim()) {
    cleaned.estimatedValueOp = filters.estimatedValueOp;
    cleaned.estimatedValueValue = filters.estimatedValueValue.trim();
  }
  if (filters.page !== DEFAULT_LEAD_LIST_FILTERS.page) cleaned.page = filters.page;
  if (filters.sort !== DEFAULT_LEAD_LIST_FILTERS.sort) cleaned.sort = filters.sort;
  if (filters.dateFrom) cleaned.dateFrom = filters.dateFrom;
  if (filters.dateTo) cleaned.dateTo = filters.dateTo;

  return cleaned;
}

export function resolveLeadSavedViewFilters(
  filters: LeadListFilters,
  searchInput: string,
): LeadListFilters {
  return { ...filters, q: searchInput };
}

export const LEAD_SORT_OPTIONS: SortOption[] = [
  { label: 'Created', sortBy: 'createdAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Created', sortBy: 'createdAt', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Lead #', sortBy: 'leadNumber', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Priority score', sortBy: 'leadScore', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Value', sortBy: 'value', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Follow-up', sortBy: 'followUpDate', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Activity', sortBy: 'lastActivityAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Priority', sortBy: 'priority', sortDir: 'desc', icon: <ArrowDown size={12} /> },
];

export const LEAD_PRIORITY_FILTERS: Array<{ value: LeadPriorityFilter; label: string }> = [
  { value: 'all', label: 'All priorities' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

export const LEAD_GRADE_FILTERS: Array<{ value: LeadGradeFilter; label: string }> = [
  { value: 'all', label: 'All grades' },
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
  { value: 'D', label: 'D' },
  { value: 'F', label: 'F' },
];

export const LEAD_SLA_FILTERS: Array<{ value: LeadSlaFilter; label: string }> = [
  { value: 'all', label: 'All SLA statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'MET', label: 'Met' },
  { value: 'BREACHED', label: 'Breached' },
];

export const LEAD_QUALIFICATION_FILTERS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All qualification statuses' },
  { value: 'UNQUALIFIED', label: 'Unqualified' },
  { value: 'QUALIFIED', label: 'Qualified' },
  { value: 'DISQUALIFIED', label: 'Disqualified' },
];

const STAGE_DOT_CLASSES = [
  'bg-blue-500',
  'bg-indigo-500',
  'bg-amber-500',
  'bg-orange-500',
  'bg-emerald-500',
  'bg-red-500',
  'bg-slate-400',
];

const PAGE_SIZE = 20;

export function buildLeadListParams(
  filters: LeadListFilters,
  limit: number = PAGE_SIZE,
): LeadListParams {
  const activeSort = LEAD_SORT_OPTIONS[filters.sort] ?? LEAD_SORT_OPTIONS[0];
  const params: LeadListParams = {
    page: filters.page,
    limit,
    search: filters.q.trim() || undefined,
    stage: filters.stage !== 'all' ? filters.stage : undefined,
    source: filters.source !== 'all' ? filters.source : undefined,
    // Passes `UNASSIGNED_LEAD_FILTER` straight through — the server reads that
    // word as "no owner", so it is a filter value here and not a placeholder to
    // translate away.
    assignedTo: filters.assignedTo !== 'all' ? filters.assignedTo : undefined,
    priority: filters.priority !== 'all' ? filters.priority : undefined,
    leadGrade: filters.leadGrade !== 'all' ? filters.leadGrade : undefined,
    qualificationStatus:
      filters.qualificationStatus !== 'all' ? filters.qualificationStatus : undefined,
    slaStatus: filters.slaStatus !== 'all' ? filters.slaStatus : undefined,
    createdAfter: filters.dateFrom,
    createdBefore: filters.dateTo,
    sortBy: activeSort.sortBy,
    sortDir: activeSort.sortDir,
  };

  // `filters.estimatedValue*` are the URL/saved-view state keys and keep their
  // names — renaming them would silently drop the filter out of every
  // bookmarked link and stored view. What goes over the wire is `valueMin/Max`,
  // which the server matches against the amount the row displays.
  const value = Number(filters.estimatedValueValue);
  if (filters.estimatedValueValue.trim() && Number.isFinite(value)) {
    if (filters.estimatedValueOp === 'eq') {
      params.valueMin = value;
      params.valueMax = value;
    }
    if (filters.estimatedValueOp === 'gt' || filters.estimatedValueOp === 'gte') {
      params.valueMin = value;
    }
    if (filters.estimatedValueOp === 'lt' || filters.estimatedValueOp === 'lte') {
      params.valueMax = value;
    }
  }

  return params;
}

interface LeadListPanelProps {
  selectedId?: string;
  onSelect: (id: string) => void;
  filters: LeadListFilters;
  onFiltersChange: (patch: Partial<LeadListFilters>) => void;
  onFiltersReplace: (filters: Partial<LeadListFilters>) => void;
}

function LeadRowMenu({
  lead,
  convertingToJob,
  autoAssigning,
  onEdit,
  onAutoAssign,
  onConvertToEstimate,
  onConvertToJob,
  onDisqualify,
  onMarkLost,
  onMerge,
  onPrint,
}: {
  lead: Lead;
  convertingToJob: boolean;
  autoAssigning: boolean;
  onEdit: (leadId: string) => void;
  onAutoAssign: (leadId: string) => void;
  onConvertToEstimate: (leadId: string) => void;
  onConvertToJob: (leadId: string) => void;
  onDisqualify: (leadId: string) => void;
  onMarkLost: (leadId: string) => void;
  onMerge: (leadId: string) => void;
  onPrint: (leadId: string) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <MoreVertical size={14} />
          <span className="sr-only">Lead actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {!lead.isClosed && (
          <DropdownMenuItem onSelect={() => onEdit(lead.id)} className="gap-2">
            <Pencil size={14} />
            Edit
          </DropdownMenuItem>
        )}
        {!lead.isClosed && (
          <DropdownMenuItem
            onSelect={() => onAutoAssign(lead.id)}
            disabled={autoAssigning}
            className="gap-2"
          >
            <Zap size={14} />
            Auto-assign
          </DropdownMenuItem>
        )}
        {!lead.isClosed && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onConvertToEstimate(lead.id)} className="gap-2">
              <FileText size={14} />
              Create estimate
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onConvertToJob(lead.id)}
              disabled={convertingToJob}
              className="gap-2"
            >
              <BriefcaseBusiness size={14} />
              Convert to job
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onMerge(lead.id)} className="gap-2">
              <GitMerge size={14} />
              Merge duplicate
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onDisqualify(lead.id)} className="gap-2">
              <Ban size={14} />
              Disqualify
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onMarkLost(lead.id)}
              className="gap-2 text-red-600 focus:text-red-600"
            >
              <XCircle size={14} />
              Mark lost
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem onSelect={() => onPrint(lead.id)} className="gap-2">
          <Printer size={14} />
          Print
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function LeadSavedViewsMenu({
  filters,
  onApply,
}: {
  filters: LeadListFilters;
  onApply: (filters: Partial<LeadListFilters>) => void;
}) {
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const savedViewsQuery = useLeadSavedViews();
  const createSavedView = useCreateLeadSavedView();
  const deleteSavedView = useDeleteLeadSavedView();
  const savedViews = savedViewsQuery.data ?? [];

  function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    createSavedView.mutate(
      {
        name: trimmedName,
        // Persist a trimmed query for saved views — unlike the live search input
        // (which keeps in-progress whitespace so multi-word terms can be typed),
        // a stored view should not carry stray leading/trailing spaces.
        filters: cleanLeadListSearch({
          ...filters,
          q: filters.q.trim(),
          page: DEFAULT_LEAD_LIST_FILTERS.page,
        }) as LeadSavedViewFilters,
      },
      {
        onSuccess: () => {
          setName('');
          setSaveOpen(false);
        },
      },
    );
  }

  return (
    <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon-sm" title="Saved views">
            <Bookmark size={14} />
            <span className="sr-only">Saved views</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Saved views</DropdownMenuLabel>
          {savedViewsQuery.isLoading ? (
            <DropdownMenuItem disabled>Loading...</DropdownMenuItem>
          ) : savedViews.length === 0 ? (
            <DropdownMenuItem disabled>No saved views</DropdownMenuItem>
          ) : (
            savedViews.map((view) => (
              <DropdownMenuItem
                key={view.id}
                onSelect={() => onApply({ ...view.filters, page: 1 })}
                className="gap-2"
              >
                <Bookmark size={14} />
                <span className="truncate">{view.name}</span>
              </DropdownMenuItem>
            ))
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              setSaveOpen(true);
            }}
            className="gap-2"
          >
            <BookmarkPlus size={14} />
            Save current view
          </DropdownMenuItem>

          {savedViews.length > 0 ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-2">
                <Trash2 size={14} />
                Delete saved view
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56">
                {savedViews.map((view) => (
                  <DropdownMenuItem
                    key={view.id}
                    variant="destructive"
                    disabled={deleteSavedView.isPending}
                    onSelect={() => deleteSavedView.mutate(view.id)}
                  >
                    <span className="truncate">{view.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <form className="space-y-4" onSubmit={handleSave}>
          <DialogHeader>
            <DialogTitle>Save lead view</DialogTitle>
            <DialogDescription className="sr-only">
              Save the current lead filters and sort order as a personal view.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="lead-saved-view-name">Name</Label>
            <Input
              id="lead-saved-view-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || createSavedView.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function LeadListPanel({
  selectedId,
  onSelect,
  filters,
  onFiltersChange,
  onFiltersReplace,
}: LeadListPanelProps) {
  const router = useRouter();
  const {
    searchInput: search,
    setSearchInput: setSearch,
    debouncedSearch,
  } = useListPanelSearch({
    value: filters.q,
    onChange: (value) => onFiltersChange({ q: value, page: 1 }),
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [markLostLeadId, setMarkLostLeadId] = useState<string | null>(null);
  const [disqualifyLeadId, setDisqualifyLeadId] = useState<string | null>(null);
  const [mergeSurvivorLeadId, setMergeSurvivorLeadId] = useState<string | null>(null);
  const stagesQuery = useLeadStages();
  const convertToJob = useConvertLeadToJob();
  const autoAssign = useAutoAssignLead();
  const stages = useMemo(() => stagesQuery.data ?? [], [stagesQuery.data]);
  const activeSort = LEAD_SORT_OPTIONS[filters.sort] ?? LEAD_SORT_OPTIONS[0];

  const dateRange: DateRange | undefined =
    filters.dateFrom || filters.dateTo
      ? {
          from: filters.dateFrom ? parseISO(filters.dateFrom) : undefined,
          to: filters.dateTo ? parseISO(filters.dateTo) : undefined,
        }
      : undefined;

  const stageOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...stages
        .filter((stage) => stage.isActive)
        .map((stage) => ({ value: stage.id, label: stage.name })),
    ],
    [stages],
  );

  const stageDotColors = useMemo(
    () =>
      stages.reduce<Record<string, string>>((acc, stage, index) => {
        acc[stage.id] = STAGE_DOT_CLASSES[index % STAGE_DOT_CLASSES.length];
        return acc;
      }, {}),
    [stages],
  );

  const assigneeOptions = useLeadAssigneeFilterOptions({ includeUserId: filters.assignedTo });

  const params = buildLeadListParams({ ...filters, q: debouncedSearch }, PAGE_SIZE);
  const savedViewFilters = useMemo(
    () => resolveLeadSavedViewFilters(filters, search),
    [filters, search],
  );

  const query = useLeads(params);
  const leads = query.data?.data ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, query.data?.totalPages ?? Math.ceil(total / PAGE_SIZE));
  const mergeSurvivorLead = mergeSurvivorLeadId
    ? leads.find((lead) => lead.id === mergeSurvivorLeadId)
    : undefined;

  function clearDate() {
    onFiltersChange({ dateFrom: undefined, dateTo: undefined, page: 1 });
  }

  const activeAdvancedCount =
    (filters.stage !== 'all' ? 1 : 0) +
    (filters.source !== 'all' ? 1 : 0) +
    (filters.assignedTo !== 'all' ? 1 : 0) +
    (filters.priority !== 'all' ? 1 : 0) +
    (filters.leadGrade !== 'all' ? 1 : 0) +
    (filters.qualificationStatus !== 'all' ? 1 : 0) +
    (filters.slaStatus !== 'all' ? 1 : 0) +
    (filters.estimatedValueValue.trim() ? 1 : 0) +
    (dateRange?.from || dateRange?.to ? 1 : 0);

  function resetAdvancedFilters() {
    onFiltersChange({
      stage: 'all',
      source: 'all',
      assignedTo: 'all',
      priority: 'all',
      leadGrade: 'all',
      qualificationStatus: 'all',
      slaStatus: 'all',
      estimatedValueOp: 'eq',
      estimatedValueValue: '',
      dateFrom: undefined,
      dateTo: undefined,
      page: 1,
    });
  }

  function handleEdit(leadId: string) {
    void router.navigate({
      to: '/dashboard/leads/$leadId/edit',
      params: { leadId },
      // @ts-expect-error — search required by TanStack Router v1.168
      search: (prev) => prev,
    });
  }

  function handleConvertToEstimate(leadId: string) {
    void router.navigate({
      to: '/dashboard/estimates/new',
      search: { manual: '1', leadId } as never,
    });
  }

  function handleConvertToJob(leadId: string) {
    convertToJob.mutate(leadId, {
      onSuccess: (data) => {
        void router.navigate({
          to: '/dashboard/jobs/$id/edit',
          params: { id: data.jobId },
          // @ts-expect-error — search required by TanStack Router v1.168
          search: (prev) => prev,
        });
      },
    });
  }

  function handlePrint(leadId: string) {
    void router
      .navigate({
        to: '/dashboard/leads/$leadId',
        params: { leadId },
        // @ts-expect-error — search required by TanStack Router v1.168
        search: (prev) => prev,
      })
      .then(() => {
        window.setTimeout(() => window.print(), 100);
      });
  }

  return (
    <>
      <EntitySplitListPanel
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search leads...',
        }}
        statusFilter={{
          value: filters.stage,
          options: stageOptions,
          dotColors: stageDotColors,
          onChange: (value) => onFiltersChange({ stage: value, page: 1 }),
        }}
        sort={{
          activeIndex: filters.sort,
          activeLabel: activeSort.label,
          options: LEAD_SORT_OPTIONS,
          onSelect: (sort) => onFiltersChange({ sort, page: 1 }),
          onReset: () => onFiltersChange({ sort: 0, page: 1 }),
        }}
        toolbarEnd={
          <>
            <LeadSavedViewsMenu filters={savedViewFilters} onApply={onFiltersReplace} />
            <EntityListAdvancedFiltersSheet
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
              activeCount={activeAdvancedCount}
              onReset={resetAdvancedFilters}
              resetDisabled={activeAdvancedCount === 0}
            >
              <DataTableFilterSection
                label="Stage"
                active={filters.stage !== 'all'}
                onClear={() => onFiltersChange({ stage: 'all', page: 1 })}
              >
                <DataTableSelectFilterField
                  value={filters.stage}
                  onValueChange={(value) => onFiltersChange({ stage: value, page: 1 })}
                  placeholder="All stages"
                  options={stageOptions.map((stage) => ({
                    value: stage.value,
                    label: stage.value === 'all' ? 'All stages' : stage.label,
                  }))}
                />
              </DataTableFilterSection>

              <DataTableFilterSection
                label="Source"
                active={filters.source !== 'all'}
                onClear={() => onFiltersChange({ source: 'all', page: 1 })}
              >
                <DataTableSelectFilterField
                  value={filters.source}
                  onValueChange={(value) =>
                    onFiltersChange({ source: value as LeadSourceFilter, page: 1 })
                  }
                  placeholder="All sources"
                  options={LEAD_SOURCE_FILTERS}
                />
              </DataTableFilterSection>

              <DataTableFilterSection
                label="Assigned to"
                active={filters.assignedTo !== 'all'}
                onClear={() => onFiltersChange({ assignedTo: 'all', page: 1 })}
              >
                <DataTableSelectFilterField
                  value={filters.assignedTo}
                  onValueChange={(value) => onFiltersChange({ assignedTo: value, page: 1 })}
                  placeholder="All assignees"
                  options={assigneeOptions}
                />
              </DataTableFilterSection>

              <DataTableFilterSection
                label="Priority"
                active={filters.priority !== 'all'}
                onClear={() => onFiltersChange({ priority: 'all', page: 1 })}
              >
                <DataTableSelectFilterField
                  value={filters.priority}
                  onValueChange={(value) => onFiltersChange({ priority: value, page: 1 })}
                  placeholder="All priorities"
                  options={LEAD_PRIORITY_FILTERS}
                />
              </DataTableFilterSection>

              <DataTableFilterSection
                label="Priority grade"
                active={filters.leadGrade !== 'all'}
                onClear={() => onFiltersChange({ leadGrade: 'all', page: 1 })}
              >
                <DataTableSelectFilterField
                  value={filters.leadGrade}
                  onValueChange={(value) =>
                    onFiltersChange({ leadGrade: value as LeadGradeFilter, page: 1 })
                  }
                  placeholder="All grades"
                  options={LEAD_GRADE_FILTERS}
                />
              </DataTableFilterSection>

              <DataTableFilterSection
                label="Qualification"
                active={filters.qualificationStatus !== 'all'}
                onClear={() => onFiltersChange({ qualificationStatus: 'all', page: 1 })}
              >
                <DataTableSelectFilterField
                  value={filters.qualificationStatus}
                  onValueChange={(value) =>
                    onFiltersChange({ qualificationStatus: value, page: 1 })
                  }
                  placeholder="All qualification statuses"
                  options={LEAD_QUALIFICATION_FILTERS}
                />
              </DataTableFilterSection>

              <DataTableFilterSection
                label="SLA"
                active={filters.slaStatus !== 'all'}
                onClear={() => onFiltersChange({ slaStatus: 'all', page: 1 })}
              >
                <DataTableSelectFilterField
                  value={filters.slaStatus}
                  onValueChange={(value) =>
                    onFiltersChange({ slaStatus: value as LeadSlaFilter, page: 1 })
                  }
                  placeholder="All SLA statuses"
                  options={LEAD_SLA_FILTERS}
                />
              </DataTableFilterSection>

              <DataTableFilterSection
                label="Value"
                active={!!filters.estimatedValueValue.trim()}
                onClear={() =>
                  onFiltersChange({ estimatedValueOp: 'eq', estimatedValueValue: '', page: 1 })
                }
              >
                <DataTableNumberFilterField
                  op={filters.estimatedValueOp}
                  value={filters.estimatedValueValue}
                  onOpChange={(op) => onFiltersChange({ estimatedValueOp: op, page: 1 })}
                  onValueChange={(value) =>
                    onFiltersChange({ estimatedValueValue: value, page: 1 })
                  }
                  placeholder="0.00"
                />
              </DataTableFilterSection>

              <DataTableFilterSection
                label="Created At"
                active={!!(dateRange?.from || dateRange?.to)}
                onClear={clearDate}
              >
                <DataTableDateRangeField
                  range={dateRange}
                  onSelect={(range) =>
                    onFiltersChange({
                      dateFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                      dateTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                      page: 1,
                    })
                  }
                />
              </DataTableFilterSection>
            </EntityListAdvancedFiltersSheet>
          </>
        }
        belowToolbar={<EntityListActiveDatePill range={dateRange} onClear={clearDate} />}
        isLoading={query.isLoading}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        errorTitle="Failed to load leads"
        emptyIcon={FileText}
        emptyTitle="No leads found"
        hasItems={leads.length > 0}
        pagination={{
          page: filters.page,
          totalPages,
          total,
          onPrevious: () => onFiltersChange({ page: Math.max(1, filters.page - 1) }),
          onNext: () => onFiltersChange({ page: Math.min(totalPages, filters.page + 1) }),
        }}
      >
        {leads.map((lead) => {
          const isSelected = selectedId === lead.id;
          const contactName = getContactName(lead);
          // Converted rows carry their money in actualValue, so reading
          // estimatedValue alone left every won lead showing no amount at all.
          const money = formatMoney(resolveLeadValueFact(lead).amount);
          // Parity with the Board (NOT-480): the person/contact name leads the
          // row; the service / AI-enrichment title ("Inbound message",
          // "contract-visit") is demoted to the secondary line — never promoted
          // over the name.
          const primary = contactName || leadTitle(lead);
          const secondaryTitle =
            isGeneratedLeadTitle(lead) || leadTitle(lead) === primary ? '' : leadTitle(lead);
          const showLeadNumber = primary !== lead.leadNumber;
          return (
            <EntityListCompactItem
              key={lead.id}
              selected={isSelected}
              onClick={() => onSelect(lead.id)}
              content={
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-1.5">
                    <p className="truncate text-sm font-medium">{primary}</p>
                    <StatusBadge
                      status={lead.stage.name}
                      label={lead.stage.name}
                      color={lead.stage.color}
                    />
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    {showLeadNumber ? (
                      <span className="text-xs text-muted-foreground">{lead.leadNumber}</span>
                    ) : null}
                    {secondaryTitle ? (
                      <>
                        {showLeadNumber ? (
                          <span className="text-xs text-muted-foreground">·</span>
                        ) : null}
                        <span className="truncate text-xs text-muted-foreground">
                          {secondaryTitle}
                        </span>
                      </>
                    ) : null}
                    {money ? <span className="ml-auto text-xs font-medium">{money}</span> : null}
                  </div>
                </div>
              }
              actions={
                <EntityRowActions>
                  <LeadRowMenu
                    lead={lead}
                    convertingToJob={convertToJob.isPending}
                    autoAssigning={autoAssign.isPending}
                    onEdit={handleEdit}
                    onAutoAssign={(leadId) => autoAssign.mutate({ leadId })}
                    onConvertToEstimate={handleConvertToEstimate}
                    onConvertToJob={handleConvertToJob}
                    onDisqualify={setDisqualifyLeadId}
                    onMarkLost={setMarkLostLeadId}
                    onMerge={setMergeSurvivorLeadId}
                    onPrint={handlePrint}
                  />
                </EntityRowActions>
              }
            />
          );
        })}
      </EntitySplitListPanel>

      {markLostLeadId && (
        <MarkLeadLostModal
          open
          leadId={markLostLeadId}
          onOpenChange={(open) => {
            if (!open) setMarkLostLeadId(null);
          }}
        />
      )}
      {disqualifyLeadId && (
        <DisqualifyLeadModal
          open
          leadId={disqualifyLeadId}
          onOpenChange={(open) => {
            if (!open) setDisqualifyLeadId(null);
          }}
        />
      )}
      {mergeSurvivorLead ? (
        <MergeLeadModal
          open
          survivor={mergeSurvivorLead}
          onOpenChange={(open) => {
            if (!open) setMergeSurvivorLeadId(null);
          }}
        />
      ) : null}
    </>
  );
}
